import { useState, useEffect, useCallback } from 'react'
import { Cloud, Settings, Activity, Key } from 'lucide-react'
import { EventoConfigSection } from './EventoConfigSection'
import { EventoMonitorSection } from './EventoMonitorSection'
import { EventoKeysSection } from './EventoKeysSection'
import { eventoSyncApi } from '@/services/api/eventoSync'
import type { EventoSettings, SyncStatus, SyncLogEntry } from '@/services/api/eventoSync'

export type SubTab = 'config' | 'monitor' | 'keys'

interface EventoSyncTabProps {
  initialSubTab?: SubTab
  onSubTabChange?: (subTab: SubTab) => void
  onSyncRunning?: (isRunning: boolean) => void
}

export function EventoSyncTab({ initialSubTab = 'config', onSubTabChange, onSyncRunning }: EventoSyncTabProps) {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>(initialSubTab)
  const [settings, setSettings] = useState<EventoSettings | null>(null)
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null)
  const [syncLogs, setSyncLogs] = useState<SyncLogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [syncLoading, setSyncLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const fetchSettings = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await eventoSyncApi.getSettings()
      setSettings(res)
    } catch (err) {
      setError('Einstellungen konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchSyncMonitor = useCallback(async () => {
    setSyncLoading(true)
    try {
      const [logsRes, statusRes] = await Promise.all([
        eventoSyncApi.getSyncHistory(20),
        eventoSyncApi.getSyncStatus(),
      ])
      setSyncLogs(logsRes.sync_logs)
      setSyncStatus(statusRes)
      if (statusRes.current_sync?.status === 'running') {
        onSyncRunning?.(true)
      }
    } catch {
      // Silently ignore
    } finally {
      setSyncLoading(false)
    }
  }, [onSyncRunning])

  useEffect(() => {
    fetchSettings()
    fetchSyncMonitor()
  }, [fetchSettings, fetchSyncMonitor])

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [success])

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [error])

  const handleSubTabChange = (subTab: SubTab) => {
    setActiveSubTab(subTab)
    onSubTabChange?.(subTab)
  }

  const handleSettingsUpdate = useCallback(async (payload: Partial<EventoSettings>) => {
    try {
      await eventoSyncApi.updateSettings(payload)
      await fetchSettings()
      setSuccess('Einstellungen gespeichert')
    } catch (err) {
      setError('Fehler beim Speichern')
    }
  }, [fetchSettings])

  const handleRefreshMonitor = useCallback(async () => {
    await fetchSyncMonitor()
  }, [fetchSyncMonitor])

  const handleSyncTriggered = useCallback(() => {
    onSyncRunning?.(true)
    fetchSyncMonitor()
  }, [onSyncRunning, fetchSyncMonitor])

  const handleSyncCompleted = useCallback(() => {
    onSyncRunning?.(false)
    fetchSyncMonitor()
    fetchSettings()
  }, [onSyncRunning, fetchSyncMonitor, fetchSettings])

  const subTabs: { key: SubTab; label: string; icon: typeof Settings }[] = [
    { key: 'config', label: 'Konfiguration', icon: Settings },
    { key: 'monitor', label: 'Sync Monitor', icon: Activity },
    { key: 'keys', label: 'API-Keys', icon: Key },
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full" />
        <span className="ml-2 text-sm text-gray-500">Laden...</span>
      </div>
    )
  }

  if (!settings) {
    return (
      <div className="p-6 text-center text-red-600">
        Einstellungen konnten nicht geladen werden
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Cloud className="w-5 h-5 text-blue-600" />
        <h2 className="text-lg font-semibold">Evento Stundenplan-Sync</h2>
      </div>

      {error && (
        <div className="p-3 bg-red-100 text-red-700 rounded flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-100 text-green-700 rounded flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-500 hover:text-green-700">&times;</button>
        </div>
      )}

      <div className="flex gap-2 mb-6 border-b">
        {subTabs.map(tab => {
          const Icon = tab.icon
          return (
            <button
              key={tab.key}
              onClick={() => handleSubTabChange(tab.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                activeSubTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          )
        })}
      </div>

      <div className="flex-1 overflow-auto">
        {activeSubTab === 'config' && (
          <EventoConfigSection
            settings={settings}
            onUpdate={handleSettingsUpdate}
            onSyncTriggered={handleSyncTriggered}
            onSyncCompleted={handleSyncCompleted}
            webhookBaseUrl={window.location.origin}
          />
        )}
        {activeSubTab === 'monitor' && (
          <EventoMonitorSection
            syncStatus={syncStatus}
            syncLogs={syncLogs}
            loading={syncLoading}
            onRefresh={handleRefreshMonitor}
            onSyncTriggered={handleSyncTriggered}
            onSyncCompleted={handleSyncCompleted}
          />
        )}
        {activeSubTab === 'keys' && (
          <EventoKeysSection
            settings={settings}
            onUpdate={handleSettingsUpdate}
          />
        )}
      </div>
    </div>
  )
}